import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderKanban, CheckCircle2, XCircle } from 'lucide-react';
import { apiClient } from '@/services/api';
import { CreateProjectForm } from '@/components/CreateProjectForm';
import { DataTable } from '@shared/components/DataTable';
import { StatusBadge } from '@shared/components/StatusBadge';
import { Modal } from '@shared/components/Modal';
import { EmptyState } from '@shared/components/EmptyState';
import type { Column } from '@shared/components/DataTable';
import type { Project, ProjectStatus } from '@shared/types';
import toast from 'react-hot-toast';

type ProjectRow = Project & Record<string, unknown>;

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const loadProjects = async () => {
    try {
      const data = await apiClient.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleCreate = async (data: { name: string; description: string; platform_id?: string; due_date?: string }) => {
    try {
      await apiClient.createProject({
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        platform_id: data.platform_id || undefined,
        due_date: data.due_date || undefined,
      });
      toast.success('Project created successfully');
      setShowCreateModal(false);
      await loadProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
  }, []);

  const filteredAndSortedProjects = useMemo(() => {
    let result = [...projects];

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter);
    }

    // Apply sorting
    if (sortKey) {
      result.sort((a, b) => {
        let aVal: string | number = '';
        let bVal: string | number = '';

        switch (sortKey) {
          case 'name':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'status':
            aVal = a.status;
            bVal = b.status;
            break;
          case 'due_date':
            aVal = a.due_date ?? '\uffff';
            bVal = b.due_date ?? '\uffff';
            break;
          case 'created_at':
            aVal = new Date(a.created_at).getTime();
            bVal = new Date(b.created_at).getTime();
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [projects, statusFilter, sortKey, sortDirection]);

  const columns: Column<ProjectRow>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_value, item) => (
        <div className="min-w-0">
          <button
            onClick={() => navigate(`/projects/${item.id}`)}
            className="text-sm font-medium text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 truncate block text-left"
          >
            {item.name as string}
          </button>
          {item.description && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {item.description as string}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_value, item) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={item.status as string} type="project" />
          {item.s3_bucket_name ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 dark:bg-green-900/20 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-3 w-3" />
              S3 Ready
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-900/20 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
              <XCircle className="h-3 w-3" />
              Storage Failed
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'customer_count',
      label: 'Customers',
      render: (_value, item) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {(item.customer_count as number) ?? 0}
        </span>
      ),
    },
    {
      key: 'upload_count',
      label: 'Uploads',
      render: (_value, item) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {(item.upload_count as number) ?? 0}
        </span>
      ),
    },
    {
      key: 'owner_name',
      label: 'Owner',
      render: (_value, item) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {(item.owner_name as string) || '—'}
        </span>
      ),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      sortable: true,
      render: (_value, item) => {
        const due = item.due_date as string | undefined;
        if (!due) return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>;
        const isOverdue = due < new Date().toISOString().split('T')[0]
          && (item.status === 'awaiting_upload' || item.status === 'in_review');
        return (
          <span className={`text-sm ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
            {formatDate(due)}
            {isOverdue && <span className="ml-1">(overdue)</span>}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (_value, item) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(item.created_at as string)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projects</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage audit projects and customer assignments.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-accent-600 hover:bg-accent-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </button>
      </div>

      {/* Status filter */}
      <div className="mb-4">
        <label htmlFor="status-filter" className="sr-only">Filter by status</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | 'all')}
          className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-accent-500 focus:ring-accent-500 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="awaiting_upload">Awaiting Upload</option>
          <option value="in_review">In Review</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Project table */}
      {!isLoading && projects.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <EmptyState
            icon={<FolderKanban className="h-12 w-12" />}
            title="No projects"
            description="Get started by creating a new audit project."
          />
        </div>
      ) : (
        <DataTable<ProjectRow>
          columns={columns}
          data={filteredAndSortedProjects as ProjectRow[]}
          loading={isLoading}
          onSort={handleSort}
          emptyMessage={
            statusFilter !== 'all'
              ? `No ${statusFilter} projects found.`
              : 'No projects available.'
          }
        />
      )}

      {/* Create project modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
      >
        <CreateProjectForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>
    </div>
  );
}
